import type { useGT } from 'gt-react';
import type { RowData } from '@/lib/tanstack-table';
import { Checkbox } from '@/components/ui/checkbox';
import { DataTableSelectAllCheckbox } from './data-table-select-all-checkbox';
import { unsortableColumn, type UnsortableColumn } from './unsortable-column';

/**
 * The leading row-selection column for a table with bulk actions: a page
 * select-all header (compiler-safe — see {@link DataTableSelectAllCheckbox})
 * and a per-row checkbox that honors `enableRowSelection`. Both stop click
 * propagation so ticking a box never triggers the row's own click (opening a
 * detail pane). Fixed width; not sortable, hideable, or resizable.
 */
export function createSelectColumn<TData extends RowData>(
  gt: ReturnType<typeof useGT>,
): UnsortableColumn<TData> {
  return unsortableColumn({
    id: 'select',
    header: ({ table }) => (
      <DataTableSelectAllCheckbox
        table={table}
        aria-label={gt('Select all', {
          context: 'table select-all rows checkbox',
        })}
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        disabled={!row.getCanSelect()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label={gt('Select row', {
          context: 'table select single row checkbox',
        })}
        onClick={(event) => event.stopPropagation()}
      />
    ),
    enableHiding: false,
    enableResizing: false,
    size: 40,
    minSize: 40,
  });
}
